import {
  BranchIcon,
  ClipboardIcon,
  GridIcon,
  LayersIcon,
  MergeIcon,
  MonitorIcon,
  ZapIcon,
} from '@persona-id/design-tokens/icons';
import { memo, useCallback, useMemo } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import styled from 'styled-components';

import EditConnectorDialog, {
  NEW_CONNECTOR_CARD_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/EditConnectorDialog';
import {
  MAX_ACTIONS_FOR_NON_ENHANCED,
  MAX_CONDITIONAL_STEP_FOR_NON_ENHANCED,
  MAX_STEPS_FOR_NON_ENHANCED,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useEnhancedLimitingForAdditional';
import {
  CombinedStepForTree,
  ScreenDimensions,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  AddStepConfig,
  ScreenFlowStepType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import {
  CONNECTOR_WIDTH,
  ScreenFlowConnector,
} from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow/ScreenFlowElements';
import ScreenFlowAddStepTooltip, {
  CARD_WIDTH as ADD_STEP_CARD_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowAddStepTooltip';
import { isInitialStep } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import Tooltip from 'dashboard/components/Tooltip';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import { WorkflowStepType } from 'dashboard/models/workflow-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import useToggle from 'lib/hooks/useToggle';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import { generateEnhancedMaxLimitReachedMessage } from 'lib/product-feature-errors';

const HIGHLIGHT_RADIUS = 20;
export const SCREEN_BETWEEN_WORKFLOWS_LABEL =
  'Cannot insert screens between\nactions and branch steps';
const CLIPBOARD_IS_EMPTY = 'Clipboard is empty';

export const screenFlowNewStepTypes: ScreenFlowStepType[] = [
  ScreenFlowStepType.Screen,
  ScreenFlowStepType.Module,
  ScreenFlowStepType.Actions,
  ScreenFlowStepType.Branching,
  ScreenFlowStepType.Connector,
  ScreenFlowStepType.PastedScreen,
];

export const stepTypeDisplayLabels: Record<
  ScreenFlowStepType,
  {
    label: string;
    description: string;
    icon: JSX.Element;
    rotationDegrees?: number;
  }
> = {
  [ScreenFlowStepType.Screen]: {
    label: 'Screen',
    description: 'Collect or display information in the flow',
    icon: <MonitorIcon size={24} />,
  },
  [ScreenFlowStepType.Module]: {
    label: 'Module',
    description: 'Use ready-made steps to build faster',
    icon: <GridIcon size={24} />,
  },
  [ScreenFlowStepType.Actions]: {
    label: 'Action sequence',
    description: 'Trigger a sequence of actions such as creating verifications',
    icon: <ZapIcon size={24} />,
  },
  [ScreenFlowStepType.Branching]: {
    label: 'Branching',
    description: 'Split the flow into different paths',
    icon: <BranchIcon size={24} />,
    rotationDegrees: 270,
  },
  [ScreenFlowStepType.Connector]: {
    label: 'Connector',
    description: 'Route to an existing screen in the flow',
    icon: <MergeIcon size={24} />,
  },
  [ScreenFlowStepType.PastedScreen]: {
    label: 'Paste',
    description: 'Paste a screen from your clipboard',
    icon: <ClipboardIcon size={24} />,
  },
  [ScreenFlowStepType.Integration]: {
    label: 'Integration',
    description: 'Use a 3rd party integration in the flow',
    icon: <LayersIcon size={24} />,
  },
};

export interface StepTypeItem {
  id: string;
  label: string;
  type: ScreenFlowStepType;
  description: string;
  icon: JSX.Element;
  tooltipLabel?: string;
  rotationDegrees?: number;
  showLearnMoreTooltip: boolean;
  disabled: boolean;
  isLimited: boolean;
}

// TODO (eric): replace with PDS popover when available
const AddStepTooltip = styled(Tooltip)<{ showAddConnectorTooltip: boolean }>`
  margin-left: ${({ showAddConnectorTooltip }) =>
    showAddConnectorTooltip ? NEW_CONNECTOR_CARD_WIDTH / 2 : ADD_STEP_CARD_WIDTH / 2}px;
  z-index: ${Z_INDICES.dropdown};
  border-radius: ${({ theme }) => theme.card.borderRadius} !important;
`;

const Container = styled.div.attrs<{ height: number }>(({ height }) => ({
  style: { height },
}))<{
  height: number;
}>`
  background: none;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${CONNECTOR_WIDTH}px;
`;

const MouseOverArea = styled.div`
  pointer-events: all;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${CONNECTOR_WIDTH}px;
  height: ${CONNECTOR_WIDTH * 3}px;
`;

const PlusSign = () => {
  return (
    <svg width='16' height='16' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M4 1.66602V6.33268'
        stroke='white'
        strokeWidth='1'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M1.66666 4H6.33332'
        stroke='white'
        strokeWidth='1'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

const AddButton = styled.button.attrs<{ active: boolean; left: number; top: number }>(
  ({ left, top }) => ({
    type: 'button',
    style: { left, top },
  }),
)<{
  active: boolean;
  left: number;
  top: number;
}>`
  position: absolute;
  cursor: pointer;
  pointer-events: auto;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${HIGHLIGHT_RADIUS * 2}px;
  height: ${HIGHLIGHT_RADIUS * 2}px;
  ${({ active, theme }) =>
    active &&
    `
    background-color: ${theme.color.primary.brand200};
    border-radius: 50%;
    transition: background-color .25s;
  `}
`;

const AddCircle = styled.div<{ isActive: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 50%;
  transition:
    background-color,
    border 0.25s;
  ${({ isActive, theme }) => {
    const diameter = isActive ? 28 : 20;
    return `
      width: ${diameter}px;
      height: ${diameter}px;
      background-color: ${
        isActive ? theme.color.primary.brand600 : theme.color.variants.primary.highlightLight
      };
      border: ${
        isActive
          ? `2px solid ${theme.color.primary.brand600}`
          : `2px solid ${theme.color.primary.brand200}`
      };
    `;
  }}
`;

const isActionsNode = (step: CombinedStepForTree | undefined): boolean => {
  return step?.type === 'workflow' && step?.workflowStepType !== WorkflowStepType.Conditional;
};

interface ScreenFlowAddStepButtonProps {
  prevStep: CombinedStepForTree;
  nextStep: CombinedStepForTree | null;
  steps: InquiryTemplateVersionStep[];
  branchIndex?: number;
  limitAddStep?: boolean;
  limitAddAction?: boolean;
  limitAddConditional?: boolean;
  height: number;
  screenDimensions?: ScreenDimensions;
  editable: boolean;
  clipboardHasStep: boolean;
  hasAccessForBasic: boolean;
  hasVerificationWritePermission: boolean;
  handleStepTypeSelected: (stepType: ScreenFlowStepType, addStepConfig: AddStepConfig) => void;
}

const ScreenFlowAddStepButton = ({
  prevStep,
  nextStep,
  steps,
  branchIndex,
  limitAddAction,
  limitAddStep,
  limitAddConditional,
  height,
  screenDimensions,
  editable,
  clipboardHasStep,
  hasAccessForBasic = false,
  hasVerificationWritePermission,
  handleStepTypeSelected,
}: ScreenFlowAddStepButtonProps) => {
  const [buttonIsActive, { enable: expandButton, disable: contractButton }] = useToggle(false);
  const [showAddStepTooltip, { enable: enableAddStepTooltip, disable: dismissAddStepTooltip }] =
    useToggle(false);

  const [
    showAddConnectorTooltip,
    { enable: enableAddConnectorTooltip, disable: dismissAddConnectorTooltip },
  ] = useToggle(false);

  const handleDismissAddStepTooltip = useCallback(() => {
    contractButton();
    dismissAddStepTooltip();
  }, [contractButton, dismissAddStepTooltip]);

  const handleDismissAddConnectorTooltip = useCallback(() => {
    contractButton();
    dismissAddConnectorTooltip();
  }, [contractButton, dismissAddConnectorTooltip]);

  const handleDismissTooltip = useCallback(() => {
    handleDismissAddConnectorTooltip();
    handleDismissAddStepTooltip();
  }, [handleDismissAddConnectorTooltip, handleDismissAddStepTooltip]);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const stepTypeItems: StepTypeItem[] = useMemo(
    () =>
      screenFlowNewStepTypes.map((stepTypeItem) => {
        const stepTypeLabels = stepTypeDisplayLabels[stepTypeItem];
        if (!hasAccessForBasic) {
          return {
            ...stepTypeLabels,
            id: stepTypeItem,
            type: stepTypeItem,
            tooltipLabel: null,
            disabled: true,
            isLimited: !hasAccessForBasic,
          };
        }

        let disabled = false;
        let tooltipLabel = null;
        let showLearnMoreTooltip = false;
        switch (stepTypeItem) {
          case ScreenFlowStepType.Actions: {
            if (limitAddAction) {
              disabled = true;
              tooltipLabel = generateEnhancedMaxLimitReachedMessage(
                'Actions',
                MAX_ACTIONS_FOR_NON_ENHANCED,
              );
              showLearnMoreTooltip = true;
              break;
            }
            /**
             * users should click on the prev or next actions node and add workflow steps directly
             * in workflow editor since actions nodes simply represent a slice of non-conditional steps
             * for the purposes of the flow view, all non-conditional (non-branching) nodes count as 'actions'
             */
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            if (isActionsNode(prevStep) || isActionsNode(nextStep)) {
              const actionsNodeSide = isActionsNode(prevStep) ? 'left' : 'right';
              disabled = true;
              tooltipLabel =
                'Cannot insert consecutive action sequences. Add additional actions ' +
                `by double-clicking the existing sequence on the ${actionsNodeSide}.`;
              break;
            }
            break;
          }
          case ScreenFlowStepType.Branching: {
            if (limitAddConditional) {
              disabled = true;
              tooltipLabel = generateEnhancedMaxLimitReachedMessage(
                'Conditional Steps',
                MAX_CONDITIONAL_STEP_FOR_NON_ENHANCED,
              );
              showLearnMoreTooltip = true;
              break;
            }
            break;
          }
          case ScreenFlowStepType.Screen: {
            if (limitAddStep) {
              // Disable if adding a screen step would put you over the quota
              disabled = true;
              tooltipLabel = generateEnhancedMaxLimitReachedMessage(
                'Steps',
                MAX_STEPS_FOR_NON_ENHANCED,
              );
              showLearnMoreTooltip = true;
              break;
            }
            if (prevStep.type === 'workflow' && nextStep?.type === 'workflow') {
              disabled = true;
              tooltipLabel = SCREEN_BETWEEN_WORKFLOWS_LABEL;
              break;
            }
            break;
          }
          case ScreenFlowStepType.Module: {
            if (!hasVerificationWritePermission) {
              disabled = true;
              tooltipLabel = 'Your user is missing edit permissions.';
              break;
            }
            if (limitAddStep) {
              // Add modules have at least 1 screen, so disable here if we're at the step limit
              disabled = true;
              tooltipLabel = generateEnhancedMaxLimitReachedMessage(
                'Steps',
                MAX_STEPS_FOR_NON_ENHANCED,
              );
              showLearnMoreTooltip = true;
              break;
            }
            if (prevStep.type === 'workflow' && nextStep?.type === 'workflow') {
              disabled = true;
              tooltipLabel = SCREEN_BETWEEN_WORKFLOWS_LABEL;
              break;
            }
            break;
          }

          case ScreenFlowStepType.Connector: {
            /**
             * permitting screens to be inserted between consecutive workflow-type nodes would then
             * require us to remove half of the steps from the original workflow and create a new
             * workflow object to save the other half.
             */
            disabled = prevStep.type === 'workflow' && nextStep?.type === 'workflow';
            tooltipLabel = disabled && SCREEN_BETWEEN_WORKFLOWS_LABEL;
            break;
          }
          case ScreenFlowStepType.PastedScreen: {
            /**
             * if the clipboard is empty show the clipboard empty message, otherwise show the
             * cannot insert at this location message
             */
            if (!clipboardHasStep) {
              tooltipLabel = CLIPBOARD_IS_EMPTY;
              disabled = true;
            } else if (prevStep.type === 'workflow' && nextStep?.type === 'workflow') {
              tooltipLabel = SCREEN_BETWEEN_WORKFLOWS_LABEL;
              disabled = true;
            }
            break;
          }
          default:
            break;
        }

        return {
          ...stepTypeDisplayLabels[stepTypeItem],
          id: stepTypeItem,
          type: stepTypeItem,
          tooltipLabel,
          showLearnMoreTooltip,
          disabled,
          isLimited: !hasAccessForBasic,
        };
      }),
    [
      nextStep,
      prevStep,
      clipboardHasStep,
      hasVerificationWritePermission,
      limitAddAction,
      limitAddConditional,
      limitAddStep,
      hasAccessForBasic,
    ],
  );

  const trailingButtonSuffix = nextStep == null ? '__branch-end' : '';
  const id = `screen-flow__add-step-button-${prevStep.token}${trailingButtonSuffix}`;

  return (
    <Container height={height}>
      <AddStepTooltip
        placement='bottom'
        tooltipShown={showAddStepTooltip || showAddConnectorTooltip}
        showAddConnectorTooltip={showAddConnectorTooltip}
        hideArrow
        tooltip={
          <OutsideClickHandler onOutsideClick={handleDismissTooltip}>
            {showAddConnectorTooltip ? (
              <EditConnectorDialog
                selectedStep={null}
                stepOptions={steps
                  .filter((step) => !isInitialStep(step) && step.name !== prevStep.token)
                  .map((step) => ({
                    title: getDisplayNameForStep(step),
                    name: step.name,
                  }))}
                handleStepSelect={(stepName) => {
                  handleDismissTooltip();
                  handleStepTypeSelected(ScreenFlowStepType.Connector, {
                    prevStep,
                    nextStep,
                    branchIndex,
                    connectingStepName: stepName,
                  });
                }}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                screenDimensions={screenDimensions}
              />
            ) : (
              <ScreenFlowAddStepTooltip
                stepTypeItems={stepTypeItems}
                handleTooltipClicked={(stepType: ScreenFlowStepType) => {
                  if (stepType === ScreenFlowStepType.Connector) {
                    enableAddConnectorTooltip();
                  } else {
                    handleDismissTooltip();
                    handleStepTypeSelected(stepType, { prevStep, nextStep, branchIndex });
                  }
                }}
              />
            )}
          </OutsideClickHandler>
        }
      >
        <MouseOverArea
          onMouseEnter={expandButton}
          onMouseLeave={() => {
            if (!showAddStepTooltip) {
              contractButton();
            }
          }}
        >
          <ScreenFlowConnector height={16} />
          {editable && prevStep.type !== UiStepType.Complete && (
            <AddButton
              id={id}
              data-test={id}
              tabIndex={0}
              left={CONNECTOR_WIDTH / 2 - HIGHLIGHT_RADIUS}
              top={height / 2 - HIGHLIGHT_RADIUS}
              active={buttonIsActive}
              disabled={showAddStepTooltip}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (showAddStepTooltip) {
                  contractButton();
                  dismissAddStepTooltip();
                } else {
                  expandButton();
                  enableAddStepTooltip();
                }
              }}
            >
              <AddCircle isActive={buttonIsActive}>{buttonIsActive && <PlusSign />}</AddCircle>
            </AddButton>
          )}
        </MouseOverArea>
      </AddStepTooltip>
    </Container>
  );
};

export default memo(ScreenFlowAddStepButton);
